/** @format */

import React from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowForward, EventAvailable, Refresh } from "@mui/icons-material";

import {
    CollectEmptyHint,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionSubtitle,
    CollectSectionTitle,
    FlexBetween,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import { IPromiseToPay } from "../../../utility/types/collectTypes";

interface Props {
    promises: IPromiseToPay[];
    isLoading: boolean;
    onRefresh: () => void;
}

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount || 0);
    } catch {
        return `$${(amount || 0).toFixed(0)}`;
    }
}

function fmtDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        });
    } catch {
        return value;
    }
}

// Whole-day difference between a promised date (UTC midnight) and today.
// Positive → still in the future, negative → past due.
function daysUntil(value?: string): number | null {
    if (!value) return null;
    const target = new Date(value).getTime();
    if (Number.isNaN(target)) return null;
    const today = new Date();
    const todayUtc = Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
    );
    return Math.round((target - todayUtc) / 86_400_000);
}

function dueLabel(days: number | null): { text: string; bg: string; fg: string } {
    if (days === null) return { text: "—", bg: "#f3f4f6", fg: "#6b7280" };
    if (days < 0)
        return {
            text: `${Math.abs(days)}d overdue`,
            bg: "#fee2e2",
            fg: "#b91c1c",
        };
    if (days === 0) return { text: "Due today", bg: "#fef3c7", fg: "#92400e" };
    if (days <= 3) return { text: `In ${days}d`, bg: "#fef9c3", fg: "#854d0e" };
    return { text: `In ${days}d`, bg: "#dbeafe", fg: "#1e40af" };
}

const PromiseToPayPanel: React.FC<Props> = ({ promises, isLoading, onRefresh }) => {
    const navigate = useNavigate();

    // Only pending promises are actionable on the dashboard, soonest first.
    const pending = React.useMemo(
        () =>
            promises
                .filter((p) => p.status === "pending")
                .sort(
                    (a, b) =>
                        new Date(a.promisedDate).getTime() -
                        new Date(b.promisedDate).getTime(),
                ),
        [promises],
    );

    const totalExpected = React.useMemo(
        () => pending.reduce((sum, p) => sum + (p.promisedAmount || 0), 0),
        [pending],
    );
    const currency = pending[0]?.currency || "USD";

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>Promises to Pay</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        Customers who committed to a payment date. We're tracking each
                        one and will auto-follow-up if a promise slips.
                    </CollectSectionSubtitle>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={onRefresh}>
                        <Refresh fontSize="small" />
                    </IconButton>
                </Tooltip>
            </CollectSectionHeader>

            {isLoading && pending.length === 0 ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            ) : pending.length === 0 ? (
                <CollectEmptyState>
                    <CollectEmptyTitle>No active promises</CollectEmptyTitle>
                    <CollectEmptyHint>
                        When a customer agrees to pay by a certain date, it shows up here
                        so you always know what's expected and when.
                    </CollectEmptyHint>
                </CollectEmptyState>
            ) : (
                <>
                    <FlexBetween
                        sx={{
                            backgroundColor: "rgba(30,193,149,0.08)",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            marginBottom: "12px",
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <EventAvailable sx={{ fontSize: 18, color: "#0f7a5b" }} />
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.82rem",
                                    fontWeight: 600,
                                    color: "#0f7a5b",
                                }}
                            >
                                {pending.length} promise{pending.length === 1 ? "" : "s"} being
                                tracked
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.9rem",
                                fontWeight: 700,
                                color: "#0f7a5b",
                            }}
                        >
                            {fmtCurrency(totalExpected, currency)} expected
                        </Typography>
                    </FlexBetween>

                    <Box>
                        {pending.slice(0, 6).map((p) => {
                            const days = daysUntil(p.promisedDate);
                            const badge = dueLabel(days);
                            const name =
                                p.customer?.displayName ||
                                p.invoice?.customerDisplayName ||
                                "Customer";
                            const email = p.customer?.primaryEmail;
                            const invoiceNo = p.invoice?.docNumber
                                ? `Invoice #${p.invoice.docNumber}`
                                : "Invoice";
                            return (
                                <FlexBetween
                                    key={p._id}
                                    sx={{
                                        py: 1.25,
                                        borderBottom: "1px solid rgba(15,23,42,0.04)",
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "rgba(15,23,42,0.02)",
                                        },
                                        "&:last-of-type": { borderBottom: "none" },
                                    }}
                                    onClick={() =>
                                        navigate(`/collect/invoices/${p.invoiceId}`)
                                    }
                                >
                                    <Box minWidth={0}>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "0.9rem",
                                                color: colors.primary,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {name}
                                        </Typography>
                                        {email && (
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.74rem",
                                                    color: "#6b7280",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {email}
                                            </Typography>
                                        )}
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontSize: "0.74rem",
                                                color: "#6b7280",
                                            }}
                                        >
                                            {invoiceNo} · Promised {fmtDate(p.promisedDate)}
                                        </Typography>
                                    </Box>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1.25}
                                        flexShrink={0}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "0.9rem",
                                                color: colors.primary,
                                            }}
                                        >
                                            {fmtCurrency(
                                                p.promisedAmount,
                                                p.currency || currency,
                                            )}
                                        </Typography>
                                        <Chip
                                            label={badge.text}
                                            size="small"
                                            sx={{
                                                backgroundColor: badge.bg,
                                                color: badge.fg,
                                                fontWeight: 600,
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontSize: "0.68rem",
                                                height: 22,
                                            }}
                                        />
                                        <ArrowForward
                                            sx={{ fontSize: 16, color: "#9ca3af" }}
                                        />
                                    </Box>
                                </FlexBetween>
                            );
                        })}
                    </Box>
                </>
            )}
        </CollectSectionCard>
    );
};

export default PromiseToPayPanel;
